import { VisionCtaButton } from '@/features/landing-page/v3/sections/vision-cta-button'
import { VisionFilmButton } from '@/features/landing-page/v3/sections/vision-film-button'
import { VisionGlobe, type GlobeThumb } from '@/features/landing-page/v3/sections/vision-globe'
import type { IAgentMarkPreset } from '@/components/superscale-agent-mark/superscale-agent-mark'
import { VisionStats } from '@/features/landing-page/v3/sections/vision-stats'
import { LP_GRID } from '@/features/landing-page/v3/lp-grid'

/**
 * lp-v3 "Our vision on fully agent-run marketing" — a grey card holding: heading + CTA (top-left), an
 * auto-rotating dotted globe whose surface surfaces agent-made creatives over time (centre), the
 * launch-film widget (bottom-left → shared media overlay), and a live-stats notification stack that
 * fans open on hover (bottom-right).
 *
 * The heading, subtitle, every stat card, and every creative <img> ship in the static HTML.
 * Interactivity is isolated to small client leaves — the globe canvas, the film button, the CTA, and
 * the rolling number wheels.
 *
 * TODO(landing-launch): this is intentionally a SYNC (non-async) component right now so the whole v3
 * tree can be CLIENT-rendered for the superuser design preview (see useLandingDesign). Because of that
 * we dropped the server-side stats SEED — VisionStats now relies on its client poll, showing 0 until
 * the first poll resolves (never invented placeholders). When the redesign actually goes live
 * (LANDING_DESIGN='new'), restore the SSR seed for SEO/no-flash: make
 * this `async`, `const initialStats = await fetch(`${NEXT_PUBLIC_API_URL}/platform-stats`,
 * {next:{revalidate:60},headers:serverEdgeOriginHeaders()})` (the edge header is required to pass
 * the ALB origin lock — see server-edge-origin.ts), and pass `<VisionStats initialData={initialStats} />`.
 *
 * Card padding: desktop 30px all sides; mobile 40 top / 20 sides+bottom.
 */

const STEP = '/assets/landing-v3/product-steps'
const CAR = '/assets/landing-page/carousel'

// Agent marks that surface on the globe between the creatives (same pop-in lifecycle). A wide, varied
// set so plenty of DISTINCT agents are live on the globe at once (not one at a time).
const GLOBE_AGENTS: IAgentMarkPreset[] = [
    'searching',
    'working',
    'processing',
    'dream',
    'thinking',
    'streaming',
    'spark',
    'ember',
]

// Varied creative pool that surfaces on the globe (spread across it in space + time by the animation).
// `aspect` = native width / height so each thumbnail pops up in its true ratio (nothing cropped).
const GLOBE_THUMBS: GlobeThumb[] = [
    { src: `${STEP}/creative-quinta.jpg`, alt: 'Fashion editorial ad — model in a printed scarf', aspect: 0.562 },
    { src: `${STEP}/creative-saye.jpg`, alt: 'Footwear ad — a hand holding a white-and-green sneaker', aspect: 0.519 },
    { src: `${STEP}/creative-avolt.jpg`, alt: 'Minimalist tech product ad', aspect: 0.561 },
    { src: `${STEP}/build-square.jpg`, alt: 'Furniture ad — a caned lounge chair, "Build for life"', aspect: 1 },
    { src: `${STEP}/product-chair.jpg`, alt: 'E-commerce product shot of a lounge chair', aspect: 0.866 },
    { src: `${CAR}/2cc5a6cac5e10415.jpeg`, alt: 'Sportswear ad — a woman spinning a basketball', aspect: 0.67 },
    { src: `${CAR}/4089132ac7df0429.jpeg`, alt: 'Luxury fashion ad — model holding an orange handbag', aspect: 0.67 },
    { src: `${CAR}/2861c283c6fe149b.png`, alt: 'Fragrance ad — amber glass perfume bottle', aspect: 0.567 },
    { src: `${CAR}/c3d90a8e65f75d5d.png`, alt: 'Cycling ad — a white road bike on a leather sofa', aspect: 0.562 },
    { src: `${CAR}/eb2fa64fe309fd83.png`, alt: 'Brand ad — a cat wearing a cardboard crown', aspect: 0.56 },
    { src: `${CAR}/9bb51c2e32b350f8.png`, alt: 'Outdoor app ad — a phone over an alpine landscape', aspect: 0.67 },
    { src: `${CAR}/d4ec3e83b2332402.png`, alt: 'Streetwear editorial ad', aspect: 0.56 },
    { src: `${CAR}/6981b73f2284f18c.jpeg`, alt: 'Out-of-home brand billboard creative', aspect: 0.56 },
    { src: `${CAR}/f1ecb119b51c7a58.jpeg`, alt: 'Fitness ad — runners on a forest trail', aspect: 0.56 },
    { src: `${CAR}/66d07db35df3b9ae.jpeg`, alt: 'Wellness flat-lay ad — a pastel starter-pack grid', aspect: 0.81 },
    { src: `${CAR}/a954e0d74ee27555.jpeg`, alt: 'Fintech ad — a hand holding a phone with a portfolio', aspect: 0.67 },
]

export function LpVisionSection() {
    return (
        // Widescreen pattern: the card BACKGROUND spans the full gutter width, the content inside
        // keeps the shared max-width column (lp-grid).
        <section className={`bg-background ${LP_GRID.cardSection} py-[60px] md:py-[40px]`}>
            <div
                className={`rounded-[24px] bg-[#F0EEEA] md:flex md:flex-col md:rounded-[30px] ${LP_GRID.cardPad} ${LP_GRID.stageMinH}`}
            >
                <div className={`${LP_GRID.content} md:flex md:flex-1 md:flex-col`}>
                    <div className="relative flex flex-col gap-8 md:block md:flex-1">
                        {/* Globe — in-flow on mobile, filling the card on desktop. `isolate` keeps the globe
                            (canvas + thumbnails) as its own stacking context, strictly behind the z-10 content
                            so creatives never slide over the notifications, heading, or CTA. */}
                        <VisionGlobe
                            thumbs={GLOBE_THUMBS}
                            agents={GLOBE_AGENTS}
                            className="isolate order-2 h-[280px] w-full md:absolute md:inset-0 md:h-auto"
                        />

                        {/* Heading + CTA — centered on mobile, top-left on desktop */}
                        <div className="relative z-10 order-1 flex flex-col items-center text-center md:max-w-[380px] md:items-start md:text-left">
                            <h2 className="text-[24px]! font-medium leading-[1.1]! tracking-[-0.01em]! text-foreground md:text-[40px]!">
                                A world in which humans and agents run marketing together
                            </h2>
                            <p className="mt-4 max-w-[360px] text-[14px]! leading-[1.35]! text-[#746C64]!">
                                Learn about our vision and values as we are building the future of humans and agents
                                creating and launching marketing together.
                            </p>
                            {/* Mobile: compact CTA + "Watch film" pill in a row (desktop shows the film card below) */}
                            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                                <VisionCtaButton />
                                <VisionFilmButton variant="pill" className="md:hidden" />
                            </div>
                        </div>

                        {/* Launch film — bottom-left on desktop (mobile shows the pill next to the CTA above) */}
                        <VisionFilmButton
                            variant="card"
                            className="z-10 hidden md:absolute md:bottom-0 md:left-0 md:block"
                        />

                        {/* Live stats — bottom of the stack on mobile, bottom-right on desktop */}
                        <div className="relative z-10 order-3 flex justify-center md:absolute md:bottom-0 md:right-0 md:block">
                            <VisionStats />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
